import { Badge, Button, Col, Collapse, Divider, Dropdown, Empty, Input, Result, Row, Skeleton, Space, Tooltip, Tree, theme } from "antd";
import type { SizeType } from "antd/es/config-provider/SizeContext";
import { useEffect, useMemo, useRef, useState } from "react";
import { getEnvironmentService } from "../../backend";
import { Outlet, useNavigate, useOutletContext, useParams } from "react-router";
import { Container, Plus } from "lucide-react";
import { App, Component, IEnvDetails } from "../../types";
import CommitTooltip from "../../components/CommitTooltip";
import DeploymentStatusTag from "../../components/DeploymentStatusTag";
import ChooseComponentModal from "../../components/modals/ChooseComponentModal";
import TemplateAvatar from "../../components/TemplateAvatar";
import ChooseAppModal from "../../components/modals/ChooseAppModal";
import { newUpdateNotificationsState } from "../../recoil/notification";
import { useRecoilValue } from "recoil";
import ComponentIcon from "../../components/ComponentIcon";
import { UpdateComponentRequestTypeEnum } from "@microtica/ms-engine-sdk";
import { useEnvironmentComponents, useAppDeployments } from "../../hooks/queries/environments";
import EnvironmentOverviewSkeleton from "../../components/skeletons/EnvironmentOverviewSkeleton";

const { Search } = Input;

export default function EnvironmentOverview() {
    const navigate = useNavigate();
    const { token } = theme.useToken();
    const { env } = useOutletContext<{ env: IEnvDetails }>();
    const updates = useRecoilValue(newUpdateNotificationsState);
    const leftContainerRef = useRef<HTMLDivElement>(null);
    const [loadingSideContainer, setLoadingSideContainer] = useState(true);
    const { projectId, envId, componentId, namespace = "", appName } = useParams() as {
        projectId: string;
        envId: string;
        componentId: string;
        namespace: string;
        appName: string;
    };
    const [filter, setFilter] = useState("");
    const [selectedComponent, setSelectedComponent] = useState<Component>();
    const [selectedApp, setSelectedApp] = useState<App>();
    const [openComponentsModal, setOpenComponentsModal] = useState(false);
    const [openClusterModal, setOpenClusterModal] = useState(false);
    const [openAppsModal, setOpenAppsModal] = useState(false);

    // Use TanStack Query for environment components with 30s auto-refresh
    const { 
        components, 
        isLoading: loading, 
        refetch: refetchComponents 
    } = useEnvironmentComponents(
        projectId, 
        envId, 
        env?.lastDeploymentId,
        {
            enabled: !!env,
            refetchInterval: 30000, // 30 seconds auto-refresh
        }
    );

    // Use TanStack Query for app deployments (only when a kubernetes component is selected)
    const { 
        data: appDeployments
    } = useAppDeployments(
        projectId,
        envId,
        selectedComponent?.clusterId || '',
        {
            enabled: !!selectedComponent?.clusterId && selectedComponent?.type === "kubernetes",
        }
    );

    // Merge app deployments into components when available
    const componentsWithAppDeployments = useMemo(() => {
        if (!appDeployments || !selectedComponent?.clusterId) return components;

        return components.map(c => {
            if (c.id === selectedComponent.id && c.apps) {
                return {
                    ...c,
                    apps: c.apps.map(a => ({
                        ...a,
                        deployment: appDeployments[`${selectedComponent.clusterId}-${a.name}`] || a.deployment,
                        loadingDeployment: false,
                    }))
                };
            }
            return c;
        });
    }, [components, appDeployments, selectedComponent?.id, selectedComponent?.clusterId]);

    // Filter components based on search
    const filteredComponents = useMemo(() => {
        if (!filter) return componentsWithAppDeployments;

        return componentsWithAppDeployments.reduce((acc, component) => {
            if (
                component.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase()) ||
                component.apps?.some(a => a.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase()))
            ) {
                acc.push({
                    ...component,
                    apps: component.apps?.filter(a => a.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase()))
                });
            }
            return acc;
        }, [] as Component[]);
    }, [componentsWithAppDeployments, filter]);

    // Refetch when updates occur
    useEffect(() => {
        if (env && updates.some(update => update.projectId === projectId)) {
            refetchComponents();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updates]);

    // Select default component when components load
    useEffect(() => {
        const selectDefaultComponent = async () => {
            if (!componentId && !appName && componentsWithAppDeployments.length) {
                setLoadingSideContainer(true);
                await handleSelectComponent(componentsWithAppDeployments, componentsWithAppDeployments[0], false);
                // Navigate to first component
                const firstComp = componentsWithAppDeployments[0];
                if (firstComp.type === "kubernetes") {
                    navigate(`/projects/${projectId}/environments/${envId}/components/${firstComp.id}/overview`, { replace: true });
                } else {
                    navigate(`/projects/${projectId}/environments/${envId}/components/${firstComp.id}/pipelines`, { replace: true });
                }
                setLoadingSideContainer(false);
            }
        };

        if (!loading) {
            selectDefaultComponent();
        }
    }, [loading, componentId, appName, componentsWithAppDeployments.length]);

    // Handle URL-based component/app selection
    useEffect(() => {
        if (!loading && componentsWithAppDeployments.length > 0) {
            if (componentId && appName) {
                const comp = componentsWithAppDeployments.find(c => c.id === componentId);
                if (comp) {
                    setSelectedComponent(comp);
                    setSelectedApp(comp.apps?.find(a => a.name === appName));
                    setLoadingSideContainer(false);
                }
            } else if (componentId) {
                const comp = componentsWithAppDeployments.find(c => c.id === componentId);
                if (comp) {
                    setSelectedComponent(comp);
                    setSelectedApp(undefined);
                    setLoadingSideContainer(false);
                }
            }
        }
    }, [loading, componentsWithAppDeployments, componentId, appName]);

    const handleSelectComponent = async (comps: Component[], component: Component, redirect: boolean = true) => {
        setSelectedComponent(comps.find(c => c.id === component.id));
        
        // App deployments are now fetched automatically via useAppDeployments hook
        // when selectedComponent changes and has a clusterId

        if (component.type === "kubernetes") {
            if (redirect) {
                navigate(`/projects/${projectId}/environments/${envId}/components/${component.id}/overview`, { replace: true });
            }
        } else if (component.type === "other") {
            // TODO: This is a temporary solution and the purpose is to migrate component type to "fargate"
            // if the component has output with name ApplicationLogs
            const { data: { outputs } } = await getEnvironmentService().getOutputsForResource(
                envId,
                component.name,
                projectId
            );

            if (outputs.some(o => o.key === "ApplicationLogs")) {
                await getEnvironmentService().updateComponent(
                    component.component?.id!,
                    projectId,
                    {
                        type: UpdateComponentRequestTypeEnum.Fargate
                    }
                );

                // Refetch components to get updated data
                await refetchComponents();
            }

            if (redirect) {
                navigate(`/projects/${projectId}/environments/${envId}/components/${component.id}/pipelines`, { replace: true });
            }
        } else {
            if (redirect) {
                navigate(`/projects/${projectId}/environments/${envId}/components/${component.id}/pipelines`, { replace: true });
            }
        }
        setSelectedApp(undefined);
    }

    const handleSelectApp = async (component: Component, appNamePath: string) => {
        const [ns, name] = appNamePath.split("/");

        const app = component.apps?.find(a => a.namespace === ns && a.name === name);

        if (app) {
            setSelectedApp(app);
            setSelectedComponent(componentsWithAppDeployments.find(c => c.id === component.id));

            if (component.type === "fargate") {
                navigate(
                    `/projects/${projectId}/environments/${envId}/components/${component.id}/apps/${app.name}/overview`,
                    { replace: true }
                );
            } else {
                navigate(
                    `/projects/${projectId}/environments/${envId}/components/${component.id}/apps/${app.namespace}/${app.name}/overview`,
                    { replace: true }
                );
            }

        }
    }


    const ComponentMetadata = ({ component }: { component: Component }) => {
        const shortSha = component.deployment?.sha?.substring(0, 7) || component.deployment?.version?.substring(0, 7);
        const branchName = component.deployment?.commitName || '';
        const displayVersion = `${branchName}@${shortSha}`;
        
        return (
            <div style={{ 
                display: "flex", 
                alignItems: "center",
                gap: "12px",
                padding: "0",
                width: "100%",
                minWidth: 0,
                overflow: "hidden"
            }}>
                {/* Main content - takes available space but respects status tag */}
                <div style={{ flex: "1 1 auto", minWidth: 0, display: "flex", flexDirection: "column", gap: "4px", overflow: "hidden" }}>
                    {/* Top row: Component name with icon */}
                    <div style={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: "8px",
                        minWidth: 0
                    }}>
                        <span style={{ 
                            fontSize: "14px", 
                            fontWeight: 600,
                            letterSpacing: "-0.01em",
                            color: "#374151",
                            lineHeight: "1.4",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                        }}>
                            {component.name}
                        </span>
                        <ComponentIcon type={component.type} size="small" />
                    </div>
                    
                    {/* Bottom row: Commit info - compact, secondary importance */}
                    <div style={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: "6px",
                        minWidth: 0,
                        overflow: "hidden"
                    }}>
                        {component.type === "kubernetes" && !component.deployment ? (
                            <span style={{ 
                                fontSize: "11px", 
                                color: "#9ca3af",
                                fontStyle: "italic"
                            }}>
                                connected cluster
                            </span>
                        ) : component.deployment ? (
                            <>
                                <CommitTooltip 
                                    commit={{
                                        message: component.deployment.message,
                                        sha: component.deployment.sha,
                                        version: component.deployment.version,
                                        branch: component.deployment.commitName,
                                        repository: component.deployment.repository,
                                        user: typeof component.deployment.user === 'string' 
                                            ? { name: component.deployment.user } 
                                            : component.deployment.user as { name?: string; avatar?: string }
                                    }}
                                    deploymentDate={component.deployment.timestamp}
                                >
                                    <a 
                                        href={component.deployment.repository ? `${component.deployment.repository}/commit/${component.deployment.sha || component.deployment.version}` : undefined}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ 
                                            fontSize: "11px",
                                            color: "var(--link-color)",
                                            fontFamily: "'JetBrains Mono', 'Fira Code', 'SF Mono', Consolas, monospace",
                                            fontWeight: 500,
                                            whiteSpace: "nowrap",
                                            flexShrink: 0,
                                            textDecoration: "none"
                                        }}
                                    >
                                        {displayVersion}
                                    </a>
                                </CommitTooltip>
                            </>
                        ) : (
                            <span style={{ 
                                fontSize: "11px", 
                                color: "#9ca3af",
                                fontStyle: "italic"
                            }}>
                                No deployment info
                            </span>
                        )}
                    </div>
                </div>
                
                {/* Status badge - fixed position, always visible */}
                {component.component && (
                    <div style={{ flexShrink: 0 }}>
                        <DeploymentStatusTag status={component.status} />
                    </div>
                )}
            </div>
        );
    }

    const ComponentApps = ({ component }: { component: Component; }) => (
        <>
            <Tree
                showLine
                style={{ marginTop: "-20px" }}
                onSelect={(appName) => handleSelectApp(component, appName[0] as string)}
                selectedKeys={[`${namespace}/${appName}`]}
                treeData={component.apps?.map(app => {
                    const { failedPods, pendingPods, runningPods, totalPods } = app.instance.status;
                    return {
                        key: `${app.namespace}/${app.name}`,
                        title: <div className="flex-justify-space-between flex-align-center">
                            <div>
                                <Space style={{ fontSize: "14px" }}>
                                    {app.name}
                                </Space>
                                <div style={{ fontSize: "11px" }}>
                                    {
                                        app.loadingDeployment ?
                                            <Skeleton.Input style={{ height: 11 }} /> :
                                            app.deployment ?
                                                <CommitTooltip
                                                    commit={{
                                                        message: app.deployment.message,
                                                        sha: app.deployment.sha,
                                                        version: app.deployment.version,
                                                        branch: app.deployment.commitName,
                                                        repository: app.deployment.repository,
                                                        user: typeof app.deployment.user === 'string' 
                                                            ? { name: app.deployment.user } 
                                                            : app.deployment.user as { name?: string; avatar?: string }
                                                    }}
                                                    deploymentDate={app.deployment.timestamp}
                                                >
                                                    <a
                                                        href={app.deployment.repository ? `${app.deployment.repository}/commit/${app.deployment.sha || app.deployment.version}` : undefined}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{ 
                                                            fontSize: "11px",
                                                            color: "var(--link-color)",
                                                            fontFamily: "'JetBrains Mono', 'Fira Code', 'SF Mono', Consolas, monospace",
                                                            fontWeight: 500,
                                                            whiteSpace: "nowrap",
                                                            textDecoration: "none"
                                                        }}
                                                    >
                                                        {(() => {
                                                            const shortSha = app.deployment.sha?.substring(0, 7) || app.deployment.version?.substring(0, 7);
                                                            const branchName = app.deployment.commitName || '';
                                                            return `${branchName}@${shortSha}`;
                                                        })()}
                                                    </a>
                                                </CommitTooltip> :
                                                undefined
                                    }
                                </div>
                            </div>
                            <div>
                                <Tooltip title={
                                    <>
                                        {failedPods > 0 && <div>{failedPods} failed pods</div>}
                                        {pendingPods > 0 && <div>{pendingPods} pending pods</div>}
                                        {runningPods > 0 && <div>{runningPods} running pods</div>}
                                    </>
                                }>
                                    <Space>
                                        {
                                            Array.from(Array(Math.min(totalPods, 4)).keys()).map(index => {
                                                if (index === 0 && failedPods > 0) {
                                                    return <Badge key={index} status="error" />
                                                } else if ((index === 0 || index === 1) && pendingPods > 0) {
                                                    return <Badge key={index} status="processing" />
                                                } else {
                                                    return <Badge key={index} status="success" />
                                                }
                                            })
                                        }
                                        {
                                            totalPods > 4 ? `+${totalPods - 4}` : undefined
                                        }
                                    </Space>
                                </Tooltip>
                            </div>
                        </div>
                    }
                })}
            />
            {
                component.type === "kubernetes" ?
                    <>
                        <Divider style={{ margin: 0 }} />
                        <div className="flex-align-center flex-justify-center">
                            <Button
                                type="link"
                                size="large"
                                style={{ fontWeight: 500, fontSize: "14px" }}
                                onClick={() => setOpenAppsModal(true)}
                            >
                                <Plus size={16} /> Add Application
                            </Button>
                        </div>
                    </> : undefined
            }
        </>
    )

    const AddComponentDropdown = (props: { size?: SizeType }) => (
        <Dropdown
            key="add-new"
            trigger={["click"]}
            menu={{
                items: [
                    {
                        key: "app",
                        onClick: () => setOpenClusterModal(true),
                        label: <div style={{ padding: "10px" }}>
                            <div style={{ fontSize: "16px", fontWeight: 500 }}>
                                Cluster + App
                            </div>
                            <div className="gray-text">
                                Deploy NextJS, ReactJS and other apps.
                            </div>
                            <Space style={{ marginTop: "3px" }}>
                                <TemplateAvatar size="xs" name={"NodeJS"} />
                                <TemplateAvatar size="xs" name={"NextJS"} />
                                <TemplateAvatar size="xs" name={"ReactJS"} />
                                <TemplateAvatar size="xs" name={"Strapi"} />
                                <TemplateAvatar size="xs" name={"Medusa"} />
                            </Space>
                        </div>
                    },
                    {
                        key: "component",
                        onClick: () => setOpenComponentsModal(true),
                        label: <div style={{ padding: "10px" }}>
                            <div style={{ fontSize: "16px", fontWeight: 500 }}>
                                Standalone Component
                            </div>
                            <div className="gray-text">
                                Deploy individual databases, clusters and other AWS services.
                            </div>
                            <Space style={{ marginTop: "3px" }} className="flex-align-center">
                                {
                                    env?.cloudProvider === "aws" ?
                                        <>
                                            <TemplateAvatar size="xs" name="AWS" />
                                            <Container size={18} style={{ color: "#326CE5" }} className="flex-align-center" />
                                            <TemplateAvatar size="xs" name="Lambda" />
                                            <TemplateAvatar size="small" name="Fargate" />
                                        </> :
                                        <>
                                            <TemplateAvatar size="xs" name="google" />
                                            <TemplateAvatar size="xs" name="terraform" />
                                            <Container size={18} style={{ color: "#326CE5" }} className="flex-align-center" />
                                        </>
                                }
                            </Space>
                        </div>
                    }
                ]
            }}
            placement="bottomRight"
        >
            <Button className="full-width" size={props.size}>
                                <Plus size={16} /> Add new
            </Button>
        </Dropdown>
    )

    const ComponentSearch = () => (
        <Row gutter={[12, 12]} style={{ marginBottom: "15px" }}>
            <Col span={17}>
                <Search
                    className="full-width"
                    defaultValue={filter}
                    allowClear
                    placeholder="Search components and apps"
                    onSearch={setFilter}
                />
            </Col>
            <Col span={7}>
                <AddComponentDropdown />
            </Col>
        </Row>
    )

    const EnvironmentPlaceholder = () => (
        <div className="flex-justify-center full-width">
            <Result
                title="Welcome to your environment!"
                icon={Empty.PRESENTED_IMAGE_SIMPLE}
                subTitle={
                    <>
                        <div>
                            It's currently blank, with no components or apps ready to roll.
                        </div>
                        <div>
                            Start by adding new components below!
                        </div>
                    </>
                }
                extra={
                    <AddComponentDropdown />
                }
            />
        </div>
    )

    // Show skeleton while env or components are loading
    const isInitialLoading = !env || loading;

    return (
        <div className="environment-overview">
            <Row gutter={16}>
                {
                    isInitialLoading ? (
                        // Show custom skeleton while loading
                        <EnvironmentOverviewSkeleton />
                    ) : componentsWithAppDeployments.length === 0 ? (
                        // Show empty state when loaded but no components
                        <EnvironmentPlaceholder />
                    ) : (
                        // Show actual components
                        <>
                            <Col xs={24} sm={24} md={8} style={{ height: "calc(100vh - 128px)", maxHeight: "calc(100vh - 128px)", display: "flex", flexDirection: "column" }} ref={leftContainerRef}>
                                <div style={{ flexShrink: 0 }}>
                                    <ComponentSearch />
                                </div>
                                <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
                                    <Collapse
                                        accordion
                                        size="large"
                                        bordered={false}
                                        className="no-bg full-width"
                                        items={filteredComponents.map(component => ({
                                            key: component.id,
                                            label: <ComponentMetadata component={component} />,
                                            children: component.isCluster && <ComponentApps component={component} />,
                                            showArrow: component.isCluster,
                                            className: component.isCluster ? "ant-collapse-item-with-content" : undefined,
                                            headerClass: selectedApp ? "ant-collapse-header-selected-children" : undefined,
                                            style: {
                                                marginBottom: 10,
                                                background: token.colorBgBase,
                                                borderRadius: token.borderRadiusLG
                                            }
                                        }))}
                                        onChange={(key) => {
                                            const component = filteredComponents.find(c => c.id === key[0]);
                                            if (component) {
                                                handleSelectComponent(filteredComponents, component);
                                            } else if (selectedComponent) {
                                                handleSelectComponent(filteredComponents, selectedComponent);
                                            }
                                        }}
                                        activeKey={selectedComponent?.id}
                                    />
                                </div>
                            </Col>
                            <Col xs={24} sm={24} md={16} style={{ display: "inline-flex", alignSelf: "stretch" }}>
                                {
                                    loadingSideContainer ? <Skeleton active paragraph={{ rows: 8 }} /> :
                                        <>
                                            {selectedComponent && !selectedApp && <Outlet context={{ env: env, component: selectedComponent }} />}
                                            {selectedComponent && selectedApp && <Outlet context={{ env: env, component: selectedComponent, app: selectedApp }} />}
                                        </>
                                }
                            </Col>
                        </>
                    )
                }
                {
                    env && openComponentsModal &&
                    <ChooseComponentModal
                        projectId={projectId}
                        env={env}
                        open={openComponentsModal}
                        onOk={async () => {
                            await refetchComponents();
                            setOpenComponentsModal(false);
                        }}
                        onCancel={() => setOpenComponentsModal(false)}
                    />
                }
                {
                    env && openClusterModal &&
                    <ChooseAppModal
                        projectId={projectId}
                        env={env}
                        open={openClusterModal}
                        onCancel={() => setOpenClusterModal(false)}
                    />
                }
                {
                    env && selectedComponent && openAppsModal &&
                    <ChooseAppModal
                        projectId={projectId}
                        env={env}
                        filter={{
                            componentId,
                            deploymentTarget: {
                                type: "kubernetes",
                                value: selectedComponent.clusterId!
                            }
                        }}
                        open={openAppsModal}
                        onCancel={() => setOpenAppsModal(false)}
                    />
                }
            </Row>
        </div>
    );
}